<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">{{ title }}</h4>
      </div>
      <div class="d-flex align-items-center">
        <export-sidebar
          sidebar-id="sidebar-organizations-export"
          storage-key="sidebar_organizations_export"
          :columns="exportColumns"
          :data-to-export="dataToExport"
          :data-mapper="mapOrganizationForExport"
          file-name="Organizations.xlsx"
          sheet-name="Organizations"
          :is-busy="isBusy"
          :items-count="totalRows"
        />
        <b-btn v-if="canEditOrganizations()" class="custom-btn-primary" :disabled="isBusy" @click="showModal(null, false)">
          <b-spinner v-if="isBusy" small/>
          <v-icon name="buttons/plus" :size="12"/>
          Новая организация
        </b-btn>
      </div>
    </header>

    <div class="page-body">
      <div class="page-filters">
        <filters-drawer
          :query.sync="filters.query"
          :is-busy="isBusy"
          @submit="onSearch"
          hide-search-button
        />
      </div>

      <template v-if="isBusy || (items && items.length > 0)">
        <div class="page-table-container">
          <b-table
            id="organizations"
            ref="organizations"
            class="custom-table mb-0 text-small"
            sticky-header
            :tbody-tr-class="tbodyRowClass"
            select-mode="multi"
            responsive
            :busy.sync="isBusy"
            :items="items"
            :fields="fields"
            no-provider-filtering
            no-provider-paging
            no-provider-sorting
            no-local-sorting
            @sort-changed="sortChanged"
            @row-clicked="onRowClicked"
            @row-dblclicked="onRowDblClicked"
          >
            <template #table-busy>
              <tables-skeleton :fields="fields" :rows="4"/>
            </template>

            <template #head(selected)="row">
              <b-form-checkbox
                class="mx-1"
                v-model="allSelected"
                :indeterminate="indeterminate"
                size="md"
                @change="selectPageRows"
              />
            </template>

            <template #cell(selected)="row">
              <b-form-checkbox
                class="mx-1"
                size="md"
                :checked="row.item.selected"
                @click.native.stop
                @change="onRowClicked(row.item)"
              />
            </template>

            <template #cell(organization_card)="row">
              <div class="org-name font-weight-bold">
                {{ row.item.name }}
              </div>
              <div class="org-contacts mb-1">
                <span class="org-address" :title="row.item.address">
                  {{ row.item.address || 'Адрес не указан' }}
                </span>
                <span class="org-phone">
                  <a :href="telUrl(row.item.phone)" style="color: #1A9167 !important;">
                    <v-icon v-if="row.item.phone" name="buttons/phone" :size="12" class="mr-1"/>
                    {{ phoneString(row.item.phone) }}
                  </a>
                </span>
              </div>
              <div class="org-tags">
                <span v-if="row.item.ogrn" class="custom-tag tag-info">ОГРН: {{ row.item.ogrn }}</span>
                <span v-if="row.item.kpp" class="custom-tag tag-success">КПП: {{ row.item.kpp }}</span>
                <span v-if="row.item.inn" class="custom-tag tag-purple">ИНН: {{ row.item.inn }}</span>
              </div>
            </template>

            <template #cell(action)="row">
              <b-dropdown variant="link" toggle-class="text-decoration-none box-shadow-none" class="custom-dropdown" no-caret>
                <template #button-content>
                  <v-icon name="buttons/ellipsis_vertical" :size="15"/>
                </template>
                <b-dropdown-item variant="success" @click.stop="showModal(row.item.id,true)">
                  <v-icon name="buttons/eye" :size="14" class="mr-2"/>
                  Смотреть
                </b-dropdown-item>
                <b-dropdown-item variant="primary" v-if="canEditOrganizations()" @click="showModal(row.item.id,false)">
                  <v-icon name="buttons/edit" :size="14" class="mr-2"/>
                  Редактировать
                </b-dropdown-item>
                <b-dropdown-item v-if="canEditOrganizations()" variant="danger" @click="onRemove(row)">
                  <v-icon name="buttons/trash" :size="14" class="mr-2"/>
                  Удалить
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
        </div>

        <div class="page-pagination">
          <pagination-footer
            use-router
            :current-page.sync="currentPage"
            :total-rows="totalRows"
            :per-page.sync="perPage"
            @page-click="onPagination"
            @per-page-change="onPerPageChange"
          >
            <template #footer-update>
              <span v-if="selected.length > 0" class="text-small mr-2">
                Выбрано: <b>{{selected.length}}</b>
              </span>
              <span v-if="selected.length > 0" class="text-small vertical-divider"/>
            </template>
          </pagination-footer>
        </div>
      </template>

      <div v-else class="empty-state-container">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>{{ isFiltered ? 'Нет данных соответствующих фильтрам.' : 'Нет созданных организаций.' }}</h5>
        <div v-if="!isFiltered">
          <p>Создайте новую организацию прямо сейчас</p>
          <b-btn v-if="canEditOrganizations()" class="custom-btn-primary mt-1" @click="showModal(null, false)">
            <v-icon name="buttons/plus" :size="12" class="mr-1"/>
            Новая организация
          </b-btn>
        </div>
      </div>
    </div>

    <organization ref="organization" @refresh="getItems" @modal-closed="onModalClosed"/>
  </div>
</template>

<script>
import organizations from "../../network/organizations";
  import helpers from "../../utilites/helpers";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import ExportSidebar from "../../utilites/commonUi/ExportSidebar.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton";
    import Organization from "./Organization";

export default {
  name: "Organizations",
  components: { TablesSkeleton, PaginationFooter, ExportSidebar, FiltersDrawer, Organization },
  mixins: [
    organizations,
    helpers, permissions, pageSearchService
  ],
  data() {
    return {
      title: 'Наши организации',
      items: [],
      fields: [
        { key: 'selected', class: 'organizations-select' },
        { key: 'organization_card', label: 'Карточка организации', class: 'text-left' },
        { key: 'action', label: '', class: 'organizations-action' },
      ],
      exportColumns: [
        { key: 'name', label: this.$t('message.customers.table.name'), visible: true, exportable: true },
        { key: 'inn', label: this.$t('message.customers.table.inn'), visible: true, exportable: true  },
        { key: 'kpp', label: this.$t('message.customers.table.kpp'), visible: true, exportable: true },
        { key: 'ogrn', label: this.$t('message.customers.table.ogrn'), visible: true, exportable: true  },
        { key: 'phone', label: this.$t('message.customers.table.phone'), visible: true, exportable: true  },
        { key: 'address', label: this.$t('message.customers.table.address'), visible: true, exportable: true  },
      ],
      sortBy: 'id',
      sortDesc: false,
      isBusy: false,
      filters: { query: '',},
      deleteRow: { id: null, name: '', },
      selected: [],
      allSelected: false,
      indeterminate: false,
    }
  },
  async created() {
    const query = this.$route.query;

    if (query && query.query) {
      this.filters.query = query.query;
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    await this.onSearch();
  },
  computed: {
    dataToExport() {
      return this.selected.length > 0 ? this.selected : this.items;
    },
    isFiltered() {
      return !!this.filters.query && this.filters.query.trim().length > 0;
    },
  },
  watch: {
    '$route.params.id': {
      handler(newId) {
        this.$nextTick(() => {
          if (newId && this.$refs.organization) {
            const isViewonly = this.$route.query.mode === 'view';
            this.$refs.organization.showModal(newId, isViewonly);
          } else if (!newId && this.$refs.organization) {
            this.$refs.organization.hideModal();
          }
        });
      }, immediate: true
    },
    selected(newValue) {
      if (newValue.length === 0) {
        this.indeterminate = false;
        this.allSelected = false;
      } else if (newValue.length === this.items.length) {
        this.indeterminate = false;
        this.allSelected = true;
      } else {
        this.indeterminate = true;
        this.allSelected = false;
      }
    },
  },
  methods: {
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      };

      this.isBusy = true;
      const result = await this.getOrganizations(data);
      this.isBusy = false;

      if (!result || !result.data) {
        this.items = [];
        this.isBusy = false;
        this.totalRows = 0;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.items = result.data.items;
      this.totalRows = result.data.total;
      this.updateSelected();
    },
    showModal(organizationId, isViewonly) {
      const canEdit = this.canEditOrganizations();

      if (!organizationId) {
        if (!canEdit) return;

        this.$refs.organization.showModal(null, false);
        return;
      }

      const isView = isViewonly || !canEdit;
      const query = isView ? { mode: 'view' } : {};

      this.$router.push({
        name: 'Organizations',
        params: { id: organizationId },
        query: query
      });
    },
    onRowDblClicked(item) {
      this.showModal(item.id, false);
    },
    onModalClosed() {
      if (this.$route.params.id) {
        this.$router.replace({
          name: 'Organizations',
          params: { id: null },
          hash: this.$route.hash,
        }).catch(()=>{});
      }
    },
    onRemove(row) {
      if (!this.canEditOrganizations()) return;

      if (row.item) {
        this.deleteRow = row.item
      }

      const title = this.$t('message.organizations.modal.removeTitle');
      const message = `${this.$t("message.organizations.modal.removeDesc")} "${this.deleteRow.name}"?`;
      this.showModalDelete(title, message, async () => {
        const result = await this.deleteOrganization(this.deleteRow);

        if (result.error) {
          this.showToast('Ошибка при удалении.', 'Ошибка', 'danger');
        } else {
          this.showToast('Организация удалена.', 'Успех', 'success');
          await this.getItems();
        }
      });
    },
    // Export
    mapOrganizationForExport(item) {
      return {
        name: item.name,
        inn: item.inn,
        kpp: item.kpp,
        ogrn: item.ogrn,
        phone: item.phone,
        address: item.address,
      };
    },
    async selectPageRows(checked) {
      this.selected = checked ? [...this.items] : [];
      this.updateSelected();
    },
    onRowClicked(item) {
      // Ищем уже выбранные
      const existItem = this.selected.findIndex(e => e.id === item.id);

      if (existItem !== -1) {
        this.selected.splice(existItem, 1);
      } else {
        this.selected.push(item);
      }
      this.updateSelected();
    },
    updateSelected() {
      if (!this.items) return;

      this.items.forEach((item) => {
        const isSelected = this.selected.some(e => e.id === item.id);
        this.$set(item, 'selected', isSelected);
      });
    },
    tbodyRowClass(item) {
      return item && item.selected ? ["b-table-row-selected", "table-primary", "pointer-cell"] : ["pointer-cell"];
    },
    // Permissions
    canEditOrganizations() {
      return this.hasPermission([PERM.ORGANIZATION_EDIT]);
    }
  },
}
</script>

<style>
.organizations-select {
  width: 50px;
}

.organizations-action {
  width: 70px;
  padding-left: 15px !important;
}

#organizations td {
  text-align: center;
  vertical-align: middle;
}

#organizations th {
  text-align: center;
  vertical-align: middle;
}

.org-contacts {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 16px;
}

.org-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>



// WEBPACK FOOTER //
// src/components/Organizations/Organizations.vue